import React, { useMemo, useState } from 'react';
import { ChevronDown, Users } from 'lucide-react';
import SettingsSectionHeader from './SettingsSectionHeader';
import { Card, CardContent } from '../ui/card';
import { parseRolePermissions, permissionLabel, filterSchoolSettingsRoles } from '../../utils/rolePermissions';
import { cn } from '../../lib/utils';

function SettingsCard({ children }) {
  return <Card className="rounded-2xl border-border shadow-sm">{children}</Card>;
}

export default function UserRolesSection({ roles = [], schoolUsers = [] }) {
  const [expandedId, setExpandedId] = useState(null);

  const rolesWithMeta = useMemo(() => {
    const counts = {};
    schoolUsers.forEach((u) => {
      const slug = u.role_slug || 'unknown';
      counts[slug] = (counts[slug] || 0) + 1;
    });
    return filterSchoolSettingsRoles(roles).map((role) => ({
      ...role,
      permissions: parseRolePermissions(role),
      userCount: counts[role.slug] || 0,
    }));
  }, [roles, schoolUsers]);

  return (
    <>
      <SettingsSectionHeader
        sectionId="roles.userRoles"
        description="System roles define what each user type can access across the ERP."
      />
      <div className="space-y-3">
        {rolesWithMeta.map((role) => {
          const open = expandedId === role.id;
          return (
            <SettingsCard key={role.id}>
              <CardContent className="p-0">
                <button
                  type="button"
                  onClick={() => setExpandedId(open ? null : role.id)}
                  className="w-full flex items-center gap-3 p-4 text-left hover:bg-muted/30 transition-colors rounded-2xl"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-500/15 text-blue-600 dark:text-blue-400">
                    <Users className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-semibold text-foreground">{role.name}</h3>
                      <span className="px-2 py-0.5 bg-blue-500/15 text-blue-600 dark:text-blue-400 rounded-full text-xs font-medium">{role.slug}</span>
                      <span className="px-2 py-0.5 bg-muted text-muted-foreground rounded-full text-xs">{role.userCount} user{role.userCount === 1 ? '' : 's'}</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{role.description || 'No description'}</p>
                  </div>
                  <ChevronDown className={cn('h-4 w-4 text-muted-foreground transition-transform', open && 'rotate-180')} />
                </button>
                {open && (
                  <div className="px-4 pb-4 pt-0 border-t border-border">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2 mt-3">Permissions ({role.permissions.length})</p>
                    <div className="flex flex-wrap gap-1.5">
                      {role.permissions.length === 0 ? (
                        <span className="text-sm text-muted-foreground">No permissions defined</span>
                      ) : (
                        role.permissions.map((p) => (
                          <span key={p} className="px-2 py-1 rounded-md bg-muted text-xs text-foreground border border-border">
                            {permissionLabel(p)}
                          </span>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </SettingsCard>
          );
        })}
      </div>
    </>
  );
}
